import { Component, Input, Output, EventEmitter, Inject } from '@angular/core';
import { TemplateReferenceInterface } from '@vendasta/email-builder/lib/_internal';
import { AppNamespace } from '@vendasta/email-builder';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { PreviewDialogComponent } from './../preview-dialog/preview-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OwnerType } from '@vendasta/email-builder';
import { take, switchMap } from 'rxjs/operators';
import { SENDER_ID_TOKEN } from '../../../../email-activity/src/dependencies';

export interface Template {
  title: string;
  created: Date;
  editorUrl: string;
  templateId: string;
  previewUrl?: string;
  updated?: Date;
  templateJson: string;
  templateHtml?: string;
  templateSubject: string;
}

export function asReference(template: Template): TemplateReferenceInterface {
  return {
    appNamespace: AppNamespace.EMAIL_LIBRARY,
    templateId: template.templateId,
  } as TemplateReferenceInterface;
}

export enum DefaultCardAction {
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  USE = 'use',
}

@Component({
  selector: 'email-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  standalone: false,
})
export class CardComponent {
  dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  senderId: string;
  subscriptions: Subscription[] = [];
  @Input() email: Template = null;
  @Input() readOnly: boolean;
  @Input() defaultAction: DefaultCardAction;
  @Input() userId: string;
  @Input() ownerType: OwnerType;
  @Output() duplicateRequested: EventEmitter<Template> = new EventEmitter<Template>();
  @Output() deleteRequested: EventEmitter<Template> = new EventEmitter<Template>();
  @Output() editRequested: EventEmitter<Template> = new EventEmitter<Template>();
  @Output() readOnlyEmailClicked: EventEmitter<Template> = new EventEmitter<Template>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private dialogRef: MatDialogRef<PreviewDialogComponent>,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    @Inject(SENDER_ID_TOKEN) readonly senderId$: Observable<string>,
  ) {}
  handleDuplicateClicked(template: Template): void {
    this.duplicateRequested.emit(template);
  }

  handleDeleteClicked(template: Template): void {
    this.deleteRequested.emit(template);
  }

  handlePreviewClicked(template: Template): void {
    if (this.defaultAction === DefaultCardAction.USE) {
      this.readOnlyEmailClicked.emit(template);
    } else {
      combineLatest([this.senderId$, this.partnerId$])
        .pipe(
          take(1),
          switchMap(([senderId, partnerId]) => {
            this.dialogRef = this.dialog.open(PreviewDialogComponent, {
              width: '800px',
              height: '600px',
              data: {
                primaryAction: this.defaultAction,
                template: template,
                userId: this.userId,
                ownerId: senderId,
                partnerId: partnerId,
              },
            });
            return this.dialogRef.afterClosed();
          }),
        )
        .subscribe((actionClicked) => {
          if (actionClicked) {
            this.handleCardAction();
          }
        });
    }
  }

  getImageSrc(imageName: string): string {
    if (this.ownerType === OwnerType.OWNER_TYPE_PARTNER) {
      imageName = '/images/' + imageName;
    }
    if (window.location.hostname === 'localhost') {
      return `/assets/${imageName}`;
    }
    if (this.ownerType === OwnerType.OWNER_TYPE_ACCOUNT_GROUP) {
      return `https://vstatic-prod.apigateway.co/business-center-client.9223372035105227902.prod/assets/${imageName}`;
    }
    return `https://vstatic-prod.apigateway.co/partner-center-client/assets/${imageName}`;
  }

  handleCardAction(): void {
    switch (this.defaultAction) {
      case DefaultCardAction.DUPLICATE:
        this.duplicateRequested.emit(this.email);
        break;
      case DefaultCardAction.EDIT:
        this.router.navigate([this.email.editorUrl], { relativeTo: this.route });
        break;
      case DefaultCardAction.USE:
        this.readOnlyEmailClicked.emit(this.email);
        break;
      default:
        this.router.navigate([this.email.editorUrl], { relativeTo: this.route });
    }
  }
  protected readonly DefaultCardAction = DefaultCardAction;
}
